/**
 * TweakModal — Tweak/edit modal with Same/Remove/Modify actions,
 * shade picker, inline SVG editor.
 */
import { useState, useEffect } from 'react';
import { parseSvgComponents, loadFontBatch } from '../utils/brandUtils.js';
import SvgEditor from './SvgEditor.jsx';
import FontPicker from './FontPicker.jsx';

export const CATEGORY_META = {
  'text-family': { title: 'Font Family', icon: 'Aa', modifyOptions: ['change font'] },
  'text-size': { title: 'Font Size', icon: '#', modifyOptions: ['bigger', 'smaller', 'much bigger', 'much smaller'] },
  'text-weight': { title: 'Font Weight', icon: 'B', modifyOptions: ['lighter', 'bolder'] },
  'text-color': { title: 'Font Color', icon: '\u25CF', modifyOptions: ['change color'] },
  'shape': { title: 'Shapes', icon: '\u25CB', modifyOptions: ['bigger', 'smaller', 'change color', 'change shape', 'more abstract', 'more geometric'] },
  'line': { title: 'Lines & Strokes', icon: '\u2014', modifyOptions: ['thicker', 'thinner', 'change color', 'dashed', 'remove'] },
};

export const ACTION_COLORS = { same: 'border-green-200 bg-green-50/50', remove: 'border-red-200 bg-red-50/50', modify: 'border-blue-200 bg-blue-50/50' };

export default function TweakModal({ asset, onClose, onGenerate, generating, editMode }) {
  const [components, setComponents] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [variantCount, setVariantCount] = useState(1);
  const [editedSvg, setEditedSvg] = useState(asset?.content || '');
  const [editorMode, setEditorMode] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);

  // Apply component modifications directly to SVG for live preview
  function applyModificationsToSvg(comps) {
    if (!asset?.content?.startsWith('<svg')) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(asset.content, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return;

    comps.forEach(c => {
      if (c.action === 'same') return;
      const textEls = svg.querySelectorAll('text, tspan');

      if (c.action === 'remove') {
        if (c.type === 'text-family' || c.type === 'text-size' || c.type === 'text-weight' || c.type === 'text-color') {
          // Removing text properties means removing the text element
          textEls.forEach(el => {
            if (c.detail?.includes(el.textContent?.trim())) el.remove();
          });
        } else if (c.type === 'shape' || c.type === 'line') {
          // Remove shapes/lines by color match
          svg.querySelectorAll('circle, ellipse, rect, polygon, path, line').forEach(el => {
            const fill = el.getAttribute('fill');
            const stroke = el.getAttribute('stroke');
            if ((c.color && (fill === c.color || stroke === c.color))) el.remove();
          });
        }
      }

      if (c.action === 'modify' && c.modification) {
        const mod = c.modification.toLowerCase();

        if (c.type === 'text-family' && mod.includes('change font to ')) {
          const newFont = c.modification.replace(/change font to /i, '').trim();
          textEls.forEach(el => {
            if (c.detail?.includes(el.textContent?.trim())) {
              el.setAttribute('font-family', `'${newFont}', sans-serif`);
            }
          });
          loadFontBatch([newFont]);
        }

        if (c.type === 'text-size') {
          textEls.forEach(el => {
            if (!c.detail?.includes(el.textContent?.trim())) return;
            const cur = parseFloat(el.getAttribute('font-size') || 36);
            if (mod === 'bigger') el.setAttribute('font-size', cur * 1.25);
            else if (mod === 'smaller') el.setAttribute('font-size', cur * 0.8);
            else if (mod === 'much bigger') el.setAttribute('font-size', cur * 1.6);
            else if (mod === 'much smaller') el.setAttribute('font-size', cur * 0.6);
          });
        }

        if (c.type === 'text-weight') {
          textEls.forEach(el => {
            if (!c.detail?.includes(el.textContent?.trim())) return;
            const cur = parseInt(el.getAttribute('font-weight') || 400);
            if (mod === 'bolder') el.setAttribute('font-weight', Math.min(900, cur + 200));
            else if (mod === 'lighter') el.setAttribute('font-weight', Math.max(100, cur - 200));
          });
        }

        if (c.type === 'text-color' && mod.includes('change color to ')) {
          const newColor = c.modification.replace(/change color to /i, '').trim();
          textEls.forEach(el => {
            if (c.detail?.includes(el.textContent?.trim())) el.setAttribute('fill', newColor);
          });
        }

        if ((c.type === 'shape' || c.type === 'line') && mod.includes('change color to ')) {
          const newColor = c.modification.replace(/change color to /i, '').trim();
          svg.querySelectorAll('circle, ellipse, rect, polygon, path, line').forEach(el => {
            const fill = el.getAttribute('fill');
            const stroke = el.getAttribute('stroke');
            if (c.color && fill === c.color) el.setAttribute('fill', newColor);
            if (c.color && stroke === c.color) el.setAttribute('stroke', newColor);
          });
        }

        if (c.type === 'shape') {
          svg.querySelectorAll('circle, ellipse, rect, polygon').forEach(el => {
            const fill = el.getAttribute('fill');
            if (c.color && fill !== c.color) return;
            if (mod === 'bigger') el.setAttribute('transform', `${el.getAttribute('transform') || ''} scale(1.25)`.trim());
            else if (mod === 'smaller') el.setAttribute('transform', `${el.getAttribute('transform') || ''} scale(0.8)`.trim());
          });
        }

        if (c.type === 'line') {
          svg.querySelectorAll('line, path').forEach(el => {
            const stroke = el.getAttribute('stroke');
            if (c.color && stroke !== c.color) return;
            const sw = parseFloat(el.getAttribute('stroke-width') || 2);
            if (mod === 'thicker') el.setAttribute('stroke-width', sw * 1.5);
            else if (mod === 'thinner') el.setAttribute('stroke-width', Math.max(0.5, sw * 0.6));
            else if (mod === 'dashed') el.setAttribute('stroke-dasharray', '8,4');
          });
        }
      }
    });

    const newSvg = new XMLSerializer().serializeToString(svg);
    setEditedSvg(newSvg);
    setHasEdits(true);
  }

  useEffect(() => {
    if (asset?.content) {
      const parsed = parseSvgComponents(asset.content);
      setComponents(parsed.length > 0
        ? parsed.map(c => ({ ...c, action: 'same', modification: '' }))
        : [{ type: 'overall', label: 'Overall style', detail: 'logo composition', color: '', action: 'same', modification: '' }]
      );
    }
  }, [asset]);

  function setAction(index, action) {
    setComponents(prev => {
      const next = prev.map((c, i) => i === index ? { ...c, action, modification: action === 'same' ? '' : c.modification } : c);
      if (editMode) applyModificationsToSvg(next);
      return next;
    });
  }

  function setModification(index, modification) {
    setComponents(prev => {
      const next = prev.map((c, i) => i === index ? { ...c, modification } : c);
      if (editMode) applyModificationsToSvg(next);
      return next;
    });
  }

  function buildPrompt() {
    const same = components.filter(c => c.action === 'same').map(c => c.label);
    const remove = components.filter(c => c.action === 'remove').map(c => c.label);
    const modify = components.filter(c => c.action === 'modify').map(c => `${c.label} \u2192 ${c.modification || 'change it'}`);

    let prompt = `Create ${variantCount} new logo variant${variantCount > 1 ? 's' : ''} based on this logo.`;
    if (same.length) prompt += ` KEEP exactly as-is: ${same.join('; ')}.`;
    if (remove.length) prompt += ` REMOVE completely: ${remove.join('; ')}.`;
    if (modify.length) prompt += ` MODIFY: ${modify.join('; ')}.`;
    if (additionalNotes.trim()) prompt += ` Additional: ${additionalNotes.trim()}`;
    // If user edited SVG inline, include it as the base
    if (editedSvg && editedSvg !== asset?.content) {
      prompt += ` Use this edited SVG as the starting point (user moved/resized/deleted elements): ${editedSvg}`;
    }
    return prompt;
  }

  const src = asset?.thumbnail_url || asset?.file_url;

  // Group by category
  const grouped = {};
  components.forEach((c, i) => {
    if (!grouped[c.type]) grouped[c.type] = [];
    grouped[c.type].push({ ...c, index: i });
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">{editMode ? 'Edit Logo' : 'More Like This'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
          </div>

          <div className="grid grid-cols-5 gap-6">
            {/* Preview / Editor */}
            <div className="col-span-2 sticky top-0">
              <div className="flex gap-1 mb-2">
                <button onClick={() => setEditorMode(false)} className={`text-[10px] px-2.5 py-1 rounded-md ${!editorMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>Tweak</button>
                <button onClick={() => setEditorMode(true)} className={`text-[10px] px-2.5 py-1 rounded-md ${editorMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>Edit Inline</button>
              </div>
              {editorMode && asset?.content?.startsWith('<svg') ? (
                <SvgEditor svgContent={editedSvg || asset.content} onChange={(s) => { setEditedSvg(s); setHasEdits(true); }} />
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center min-h-[200px]">
                  {editedSvg && hasEdits ? (
                    <div dangerouslySetInnerHTML={{ __html: editedSvg }} className="max-w-full [&>svg]:max-h-48 [&>svg]:mx-auto" />
                  ) : src ? <img src={src} alt="Logo" className="max-w-full max-h-48 object-contain" /> :
                   asset?.content?.startsWith('<svg') ? <div dangerouslySetInnerHTML={{ __html: asset.content }} className="max-w-full [&>svg]:max-h-48 [&>svg]:mx-auto" /> :
                   <span className="text-gray-300">No preview</span>}
                </div>
              )}
            </div>

            {/* Components */}
            <div className="col-span-3 space-y-4 max-h-[50vh] overflow-y-auto pr-1">
              {Object.entries(grouped).map(([cat, items]) => {
                const meta = CATEGORY_META[cat] || { title: cat, icon: '?', modifyOptions: [] };
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-bold text-gray-400 w-5 text-center">{meta.icon}</span>
                      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{meta.title}</h4>
                    </div>
                    <div className="space-y-2">
                      {items.map(c => (
                        <div key={c.index} className={`rounded-lg border p-2.5 transition-colors ${ACTION_COLORS[c.action]}`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-800">{c.label}</div>
                              {c.detail && <div className="text-[10px] text-gray-400">{c.detail}</div>}
                            </div>
                            {c.color && c.color !== 'none' && c.color !== '' && (
                              <div className="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0" style={{ backgroundColor: c.color }} />
                            )}
                          </div>
                          {/* Action selector */}
                          <div className="flex gap-1">
                            {['same', 'remove', 'modify'].map(action => (
                              <button key={action} onClick={() => setAction(c.index, action)}
                                className={`text-[10px] px-2.5 py-1 rounded-md font-medium transition-colors ${c.action === action
                                  ? action === 'same' ? 'bg-green-600 text-white' : action === 'remove' ? 'bg-red-500 text-white' : 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                                {action === 'same' ? 'Same' : action === 'remove' ? 'Remove' : 'Modify'}
                              </button>
                            ))}
                          </div>
                          {/* Modify options */}
                          {c.action === 'modify' && (
                            <div className="mt-2 space-y-1.5">
                              <div className="flex flex-wrap gap-1">
                                {(meta.modifyOptions || []).map(opt => (
                                  <button key={opt} onClick={() => setModification(c.index, opt)}
                                    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${c.modification === opt ? 'bg-blue-100 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-blue-200'}`}>
                                    {opt}
                                  </button>
                                ))}
                              </div>
                              {c.type === 'text-color' || c.type === 'shape' || c.type === 'line' ? (
                                c.modification === 'change color' && (
                                  <input type="color" defaultValue={c.color || '#000000'} onChange={e => setModification(c.index, `change color to ${e.target.value}`)} className="w-8 h-6 rounded cursor-pointer" />
                                )
                              ) : null}
                              {c.type === 'text-family' && c.modification === 'change font' && (
                                <FontPicker value="" onChange={v => setModification(c.index, `change font to ${v}`)} />
                              )}
                              <input type="text" value={c.modification} onChange={e => setModification(c.index, e.target.value)} placeholder="or type custom..." className="text-[11px] px-2 py-1 border border-gray-200 rounded-md w-full focus:outline-none focus:border-blue-400" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Additional notes */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Additional direction</label>
            <textarea value={additionalNotes} onChange={e => setAdditionalNotes(e.target.value)} rows={2}
              placeholder="e.g. Make it feel more premium, try a monogram style..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none" />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-5">
            {editMode ? (
              <>
                <button onClick={() => onGenerate(null, 0, editedSvg)} disabled={!hasEdits || generating}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-500 disabled:opacity-40">
                  {generating ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => { setEditedSvg(asset?.content || ''); setHasEdits(false); setComponents(prev => prev.map(c => ({ ...c, action: 'same', modification: '' }))); }}
                  className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm hover:bg-gray-50">Reset</button>
              </>
            ) : (
              <>
                <select value={variantCount} onChange={e => setVariantCount(parseInt(e.target.value))} className="px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white">
                  <option value={1}>1 variant</option>
                  <option value={2}>2 variants</option>
                  <option value={3}>3 variants</option>
                </select>
                <button onClick={() => onGenerate(buildPrompt(), variantCount)} disabled={generating}
                  className="flex-1 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-700 disabled:opacity-40">
                  {generating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Generating...
                    </span>
                  ) : 'Generate'}
                </button>
              </>
            )}
            <button onClick={onClose} className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
